import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { AgentGroup, getCategorizedAgents } from '../../services/api/categorizedAgentService';
import { getRecentHistory, getOlderHistory } from '../../services/api/historyService';
import chatService from '../../services/api/chatService';

// 履歴項目の型定義
interface HistoryItem {
  id: number;
  title: string;
  created_at: string;
  is_favorited?: boolean;
  first_user_message?: string | null;
  agent_name?: string;
  agent_color?: string;
  agent_model_type?: string;
}

// サイドバー状態の型定義
interface SidebarState {
  agentGroups: AgentGroup[];
  recentItems: HistoryItem[];
  olderItems: HistoryItem[];
  favoriteItems: HistoryItem[];
  isLoadingAgents: boolean;
  isLoadingHistory: boolean;
  error: string | null;
}

// 初期状態
const initialState: SidebarState = {
  agentGroups: [],
  recentItems: [],
  olderItems: [],
  favoriteItems: [],
  isLoadingAgents: false,
  isLoadingHistory: false,
  error: null
};

// カテゴリ別エージェント取得アクション
export const fetchCategorizedAgents = createAsyncThunk(
  'sidebar/fetchCategorizedAgents',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCategorizedAgents();
      return {
        personalAgents: response.personalAgents,
        organizationAgents: response.organizationAgents
      };
    } catch (error) {
      console.error('カテゴリ別エージェント取得エラー:', error);
      return rejectWithValue('カテゴリ別エージェントの取得に失敗しました');
    }
  }
);

// 最近の履歴取得アクション
export const fetchRecentHistory = createAsyncThunk(
  'sidebar/fetchRecentHistory',
  async (_, { rejectWithValue }) => {
    try {
      return await getRecentHistory();
    } catch (error) {
      console.error('最近の履歴取得エラー:', error);
      return rejectWithValue('最近の履歴の取得に失敗しました');
    }
  }
);

// 過去の履歴取得アクション
export const fetchOlderHistory = createAsyncThunk(
  'sidebar/fetchOlderHistory',
  async (_, { rejectWithValue }) => {
    try {
      return await getOlderHistory();
    } catch (error) {
      console.error('過去の履歴取得エラー:', error);
      return rejectWithValue('過去の履歴の取得に失敗しました');
    }
  }
);

// チャット削除アクション
export const deleteChat = createAsyncThunk(
  'sidebar/deleteChat',
  async (chatId: number, { rejectWithValue }) => {
    try {
      await chatService.deleteChat(chatId);
      return chatId;
    } catch (error) {
      console.error('チャット削除エラー:', error);
      return rejectWithValue('チャットの削除に失敗しました');
    }
  }
);

// チャットのお気に入り状態を切り替えるアクション
export const toggleChatFavorite = createAsyncThunk(
  'sidebar/toggleChatFavorite',
  async (chatId: number, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      // 現在のお気に入り状態を確認
      const allItems = [
        ...state.sidebar.favoriteItems,
        ...state.sidebar.recentItems,
        ...state.sidebar.olderItems
      ];
      const item = allItems.find(i => i.id === chatId);
      const isFavorited = item?.is_favorited || false;

      return { chatId, isFavorited: !isFavorited };
    } catch (error) {
      console.error('お気に入り切り替えエラー:', error);
      return rejectWithValue('お気に入りの切り替えに失敗しました');
    }
  }
);

// チャットのタイトルを更新するアクション
export const updateChatTitle = createAsyncThunk(
  'sidebar/updateChatTitle',
  async ({ chatId, title }: { chatId: number; title: string }, { rejectWithValue }) => {
    try {
      const response = await chatService.updateChatTitle(chatId, title);
      return { chatId, title: response.data.title };
    } catch (error) {
      console.error('タイトル更新エラー:', error);
      return rejectWithValue('タイトルの更新に失敗しました');
    }
  }
);

// サイドバースライス
const sidebarSlice = createSlice({
  name: 'sidebar',
  initialState,
  reducers: {
    clearSidebarError: (state) => {
      state.error = null;
    },
    // お気に入りアイテムをローカルで更新
    updateFavoriteItems: (state) => {
      const allItems = [
        ...state.recentItems,
        ...state.olderItems
      ];
      state.favoriteItems = allItems.filter(item => item.is_favorited);
    },
    // 新しいチャットを履歴の先頭に追加（即座に表示するため）
    addNewChatToHistory: (state, action: PayloadAction<{
      id: number;
      title: string;
      agent_name?: string;
      agent_color?: string;
      agent_model_type?: string;
    }>) => {
      const newItem: HistoryItem = {
        id: action.payload.id,
        title: action.payload.title,
        created_at: new Date().toISOString(),
        is_favorited: false,
        agent_name: action.payload.agent_name,
        agent_color: action.payload.agent_color,
        agent_model_type: action.payload.agent_model_type
      };
      // 重複を避けるため、既存のアイテムを除外してから先頭に追加
      state.recentItems = [
        newItem,
        ...state.recentItems.filter(item => item.id !== action.payload.id)
      ];
    },
    // チャットのタイトルをローカルで更新
    updateChatTitleLocal: (state, action: PayloadAction<{ chatId: number; title: string }>) => {
      const { chatId, title } = action.payload;
      const updateItemTitle = (item: HistoryItem) => {
        if (item.id === chatId) {
          return { ...item, title };
        }
        return item;
      };
      state.recentItems = state.recentItems.map(updateItemTitle);
      state.olderItems = state.olderItems.map(updateItemTitle);
      state.favoriteItems = state.favoriteItems.map(updateItemTitle);
    }
  },
  extraReducers: (builder) => {
    // カテゴリ別エージェント取得
    builder
      .addCase(fetchCategorizedAgents.pending, (state) => {
        state.isLoadingAgents = true;
        state.error = null;
      })
      .addCase(fetchCategorizedAgents.fulfilled, (state, action) => {
        state.isLoadingAgents = false;
        state.agentGroups = [
          action.payload.personalAgents,
          action.payload.organizationAgents
        ];
        state.error = null;
      })
      .addCase(fetchCategorizedAgents.rejected, (state, action) => {
        state.isLoadingAgents = false;
        state.error = action.payload as string;
      })
      
      // 最近の履歴取得
      .addCase(fetchRecentHistory.pending, (state) => {
        state.isLoadingHistory = true;
        state.error = null;
      })
      .addCase(fetchRecentHistory.fulfilled, (state, action) => {
        state.isLoadingHistory = false;
        state.recentItems = action.payload;
        state.error = null;
      })
      .addCase(fetchRecentHistory.rejected, (state, action) => {
        state.isLoadingHistory = false;
        state.error = action.payload as string;
      })
      
      // 過去の履歴取得
      .addCase(fetchOlderHistory.pending, (state) => {
        state.isLoadingHistory = true;
        state.error = null;
      })
      .addCase(fetchOlderHistory.fulfilled, (state, action) => {
        state.isLoadingHistory = false;
        state.olderItems = action.payload;
        state.error = null;
      })
      .addCase(fetchOlderHistory.rejected, (state, action) => {
        state.isLoadingHistory = false;
        state.error = action.payload as string;
      })
      
      // チャット削除
      .addCase(deleteChat.fulfilled, (state, action) => {
        const chatId = action.payload;
        state.recentItems = state.recentItems.filter(item => item.id !== chatId);
        state.olderItems = state.olderItems.filter(item => item.id !== chatId);
        state.favoriteItems = state.favoriteItems.filter(item => item.id !== chatId);
      })
      .addCase(deleteChat.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      
      // お気に入り切り替え
      .addCase(toggleChatFavorite.fulfilled, (state, action) => {
        const { chatId, isFavorited } = action.payload;

        // すべてのリストで該当チャットのお気に入り状態を更新
        const updateItem = (item: HistoryItem) => {
          if (item.id === chatId) {
            item.is_favorited = isFavorited;
          }
          return item;
        };

        state.recentItems = state.recentItems.map(updateItem);
        state.olderItems = state.olderItems.map(updateItem);

        // お気に入りリストを再構築
        const allItems = [...state.recentItems, ...state.olderItems];
        state.favoriteItems = allItems.filter(item => item.is_favorited);
      })
      .addCase(toggleChatFavorite.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // タイトル更新
      .addCase(updateChatTitle.fulfilled, (state, action) => {
        const { chatId, title } = action.payload;

        // すべてのリストで該当チャットのタイトルを更新
        const updateItemTitle = (item: HistoryItem) => {
          if (item.id === chatId) {
            item.title = title;
          }
          return item;
        };

        state.recentItems = state.recentItems.map(updateItemTitle);
        state.olderItems = state.olderItems.map(updateItemTitle);
        state.favoriteItems = state.favoriteItems.map(updateItemTitle);
      })
      .addCase(updateChatTitle.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  }
});

// セレクター
export const selectAgentGroups = (state: RootState) => state.sidebar.agentGroups;
export const selectRecentHistory = (state: RootState) => state.sidebar.recentItems;
export const selectOlderHistory = (state: RootState) => state.sidebar.olderItems;
export const selectFavoriteHistory = (state: RootState) => state.sidebar.favoriteItems;
export const selectIsLoadingAgents = (state: RootState) => state.sidebar.isLoadingAgents;
export const selectIsLoadingHistory = (state: RootState) => state.sidebar.isLoadingHistory;
export const selectSidebarError = (state: RootState) => state.sidebar.error;

export const { clearSidebarError, updateFavoriteItems, addNewChatToHistory, updateChatTitleLocal } = sidebarSlice.actions;

export default sidebarSlice.reducer;
